/**
 * Buffers out-of-order events and delivers them in sequence by eventIndex.
 *
 * Events from SSE/Supabase channels can arrive out of order because broadcast
 * sends are not awaited. Since UI rendering and state depend on correct event
 * ordering, we buffer events that arrive "early" and only deliver them once
 * all preceding events have been received.
 *
 * The consumer detects stream completion by observing the terminal event
 * (finish/error) delivered through onEvent — which only fires once all
 * preceding events have been delivered in order.
 */
export const createEventReorderer = <TEvent extends { eventIndex: number }>({
  onEvent,
  startIndex = 0,
}: {
  onEvent: (event: TEvent) => void;
  startIndex?: number;
}) => {
  let nextExpectedIndex = startIndex;
  const pending = new Map<number, TEvent>();

  const flushInOrder = () => {
    while (pending.has(nextExpectedIndex)) {
      const event = pending.get(nextExpectedIndex)!;
      pending.delete(nextExpectedIndex);
      nextExpectedIndex++;
      onEvent(event);
    }
  };

  const push = (envelope: TEvent) => {
    const index = envelope.eventIndex;

    // Drop old events (might be from replay)
    if (index >= nextExpectedIndex) {
      pending.set(index, envelope);
      flushInOrder();
    }
  };

  return { push };
};
